// E4 무빈소 퍼널 개편(신뢰 구간) 상수. 명세: wiki/plans/mubinso-funnel-trust.md
//
// 현행과 흐름이 다르다.
//   현행: 랜딩 → Q1 궁금한 점 → Q2 지금 상황 → (임종·임박이면 메인 퍼널, 아니면 Q1 대로)
//   E4  : 랜딩 = Q1 궁금한 점 버튼 + 급한 고객 버튼(한 화면) → 바로 E4 퍼널 → 결과 → 상황 선택 + 번호
//         (절차 · 모두 → 절차 퍼널 B, 비용 → 비용 퍼널 C, 장례식장 → 장례식장 퍼널 D)
// 급한 고객은 첫 화면 버튼으로, 퍼널 고객의 상황은 리드 폼에서 받는다.

import { MUBINSO_SITUATION_OPTIONS } from "./mubinsoConstants";

/** 첫 화면. Figma 「무빈소 장례 인트로」(22589:23997) */
export const MUBINSO_E4_LANDING = {
  // 「아직 낯선 방식이라…」 대신 고이 박스 문장을 설명으로 올렸다(주완 2026-10-30).
  // 「고이가」 를 앞에 붙이면 한 줄(325px)이 360px 글 폭(312px)을 넘어 「함께하며」 뒤에서 끊는다(251 / 301px)
  sub: "고이가 무빈소 장례 2,000건을 함께하며\n가족분들이 가장 많이 물어보신 것들만 담았어요.",
  // 첫 화면 버튼(= Q1 답). 굵은 제목 + 아래 설명(가족장 분기 버튼과 같은 모양). MUBINSO_TOPIC_OPTIONS 의 funnel 로 찾는다.
  // 「무빈소」 는 제목에 두고 설명은 겹치는 앞부분을 빼 한 줄로(주완 2026-10-30). 360px 설명 칸 208px 안에 모두 들어간다
  topicButtons: {
    procedure: {
      icon: "icon_Graphic_Guidebook",
      title: "무빈소 장례 절차",
      desc: "절차가 어떻게 진행되는지 궁금해요",
    },
    // 가격표는 「모두 알아보기」(돋보기 문서)와 같은 파란 종이라 구분이 안 돼 지폐로(주완 2026-10-30)
    cost: {
      icon: "icon_Graphic_PriceDrop",
      title: "무빈소 장례 비용",
      desc: "구체적인 실제 비용이 궁금해요",
    },
    parlorFind: {
      icon: "icon_Graphic_Building",
      title: "무빈소 장례식장 찾기",
      desc: "우리 지역의 장례식장을 찾고 싶어요",
    },
    main: {
      icon: "icon_Graphic_Examine",
      title: "모두 알아보기",
      desc: "절차 · 비용 · 장례식장이 다 궁금해요",
    },
  },
  // 설명 줄: 「급하다」 만으로는 임종 직후 · 임박 고객이 자기 버튼인지 알기 어려워 더했다(주완 2026-10-31).
  // 제목은 질문, 설명은 「~때」 로 끝을 달리하고, 「임종」 은 한 번만 쓴다(「위독」 · 「임박」 은 무거워 뺐다).
  // E4 는 Q2(임종 · 임박)가 없어 이 버튼이 그 역할을 대신한다. 360px 설명 칸 206px 안(156px)
  urgent: {
    icon: "icon_Graphic_CS",
    title: "당장 장례가 급하신가요?",
    desc: "임종하셨거나 앞두고 계실 때",
  },
} as const;

/** 첫 화면 급한 고객 모달. Figma 「무료 정보 신청」(22614:21243) */
export const MUBINSO_E4_URGENT_MODAL = {
  title: "지금 바로 도와드릴게요",
  description: "연락 가능한 번호를 남겨주시면 고이에서 최대한 빠르게 장례를 도와드리겠습니다.",
  // 체크박스 없이 버튼으로 필수 약관 동의를 받아 「동의하고」 를 붙인다(E4 결과 모달과 같은 기준, 영민 리뷰)
  buttonText: "동의하고 지금 바로 연락받기",
} as const;

/**
 * 결과 모달의 상황 선택. Q2 가 없어서 여기서 받는다(노션 2-4-4 「임종 선택 퍼널 → 리드 제출 폼으로」).
 * 선택지는 Q2 와 같은 문구를 써야 mubinsoLeadStatus 가 리드 status 로 바꿀 수 있다.
 * 미리 골라 두지 않는다(첫 선택지 "임종하신 상태" 로 쏠림 방지, 가족장과 같은 이유).
 */
export const MUBINSO_E4_STATUS_SELECT = {
  label: "상황을 선택해주세요. 맞춤형 안내를 드릴게요.",
  options: MUBINSO_SITUATION_OPTIONS.map((o) => o.label),
};

/**
 * E4 B·C·D 결과 모달 공통 설정. 상황 선택을 필수로 받고, 다 채우기 전에는 버튼을 끄고, 넓게 띄운다.
 * 가족장 결과 모달과 같은 조합이다. leadStatus 는 넘기지 않는다(선택값을 status 로 바꿔 보낸다).
 */
export const MUBINSO_E4_LEAD_MODAL_PROPS = {
  statusSelect: MUBINSO_E4_STATUS_SELECT,
  disableUntilFilled: true,
  wide: true,
} as const;

/** 슬랙 "궁금한 점" 자리. 첫 화면 급한 고객 리드임을 영업팀이 알아보게 한다 */
export const MUBINSO_E4_URGENT_TOPIC = "무빈소 · 첫 화면 급한 상담 요청";
